import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import type { EmailTemplate } from '@/api/types';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import { SequenceTimelineEditor } from './SequenceTimelineEditor';
import styles from './sequenceEditor.module.css';

type Props = {
  projectId: string;
  templates: EmailTemplate[];
};

export function SequenceLibraryWorkspace({ projectId, templates }: Props) {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');

  const seqQ = useQuery({
    queryKey: ['sequences', projectId],
    queryFn: () => apiFetch<{ sequences: { id: string; name: string }[] }>(`/api/sequences?project_id=${projectId}`),
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => apiFetch(`/api/sequences/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setSelectedId(null);
      void qc.invalidateQueries({ queryKey: ['sequences', projectId] });
    },
  });

  const createM = useMutation({
    mutationFn: () =>
      apiFetch<{ sequence: { id: string } }>('/api/sequences', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          name: newName.trim(),
          steps: [
            {
              step_order: 0,
              wait_days: 0,
              subject_template: 'Hoi {{voornaam}}',
              body_text_template: '…',
            },
            {
              step_order: 1,
              wait_days: 3,
              subject_template: 'Re: {{voornaam}}',
              body_text_template: 'Even follow-up…',
            },
          ],
        }),
      }),
    onSuccess: (data) => {
      setNewName('');
      setSelectedId(data.sequence.id);
      void qc.invalidateQueries({ queryKey: ['sequences', projectId] });
    },
  });

  const sequences = seqQ.data?.sequences ?? [];

  return (
    <div className={styles.workspace}>
      <aside className={styles.listPanel}>
        <h2 className={styles.listTitle}>Sequences</h2>
        <form
          className={styles.branchRow}
          onSubmit={(e) => {
            e.preventDefault();
            if (newName.trim()) createM.mutate();
          }}
        >
          <Field label="Nieuwe sequence">
            <TextInput value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Naam" />
          </Field>
          <Button type="submit" disabled={!newName.trim() || createM.isPending}>
            Aanmaken
          </Button>
        </form>
        <ul className={styles.sequenceList}>
          {sequences.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                className={[
                  styles.sequenceListBtn,
                  selectedId === s.id ? styles.sequenceListBtnActive : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => setSelectedId(s.id)}
              >
                {s.name}
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <div>
        {selectedId ? (
          <>
            <div className={styles.branchRow} style={{ justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
              <Button
                type="button"
                variant="ghost"
                disabled={deleteM.isPending}
                onClick={() => {
                  const name = sequences.find((s) => s.id === selectedId)?.name ?? 'sequence';
                  if (
                    window.confirm(
                      `"${name}" verwijderen? Alleen mogelijk als geen campagne of A/B-variant deze sequence gebruikt.`,
                    )
                  ) {
                    deleteM.mutate(selectedId);
                  }
                }}
              >
                Sequence verwijderen
              </Button>
            </div>
            <SequenceTimelineEditor projectId={projectId} sequenceId={selectedId} templates={templates} />
            {deleteM.isError ? (
              <p className={styles.simPath} style={{ color: 'var(--color-danger, #b42318)' }}>
                {(deleteM.error as Error).message}
              </p>
            ) : null}
          </>
        ) : (
          <p className={styles.simPath}>Selecteer een sequence of maak een nieuwe aan.</p>
        )}
      </div>
    </div>
  );
}
